import _ from 'lodash';
import { IResponse, Response, ResponseContext } from '../Response/Response';
import { IResponseStore } from '../Response/ResponseStore';
import { IRootStore } from '../Root/RootStore';
import { ITreeNav } from './TreeNav';
import { Instance, SnapshotIn, cast, detach, getRoot, types } from 'mobx-state-tree';

const Exercise = types.model('Exercise', {
  decided: types.optional(types.boolean, false),
  exerciseId: types.maybe(types.string),
}).views(self => ({
  get isValid() {
    return self.decided;
  },
}));

export const Choices = types.model('Choices', {
  pending: types.maybe(Response),
  exercise: types.optional(Exercise, {}),
})
  .actions(self => ({
    setPending(response: SnapshotIn<IResponse>) {
      console.log('Choices.setPending', JSON.stringify(response, undefined, 2));
      const rootStore = getRoot<IRootStore>(self);
      const treeNav = rootStore.treeNav as ITreeNav;
      const surveyId = treeNav.content.surveyId;
      const pathId = treeNav.content.path?._id;
      const sessionId = treeNav.content.session?._id;
      // use of cloneDeep here is a workaround for mobx-state-tree's handling of arrays
      // mobx will (incorrectly) fail a runtime typecheck for this array type when it is an instance, but not when it is a snapshot.
      // https://github.com/mobxjs/mobx-state-tree/issues/1459
      const answers = response.answers?.map(a => ({...a, meta: _.cloneDeep(a.meta)}));
      self.pending = cast({
        surveyId,
        pathId,
        sessionId,
        ...response,
        answers,
      });
    },
    /**
     * Choose the user choice (next question) based on nextQuestionKey.
     * Use case: when the next question is decided server-side.
     */
    setNextQuestion(nextQuestionKey: string) {
      console.log('Choices.setNextQuestion', nextQuestionKey);
      const rootStore = getRoot<IRootStore>(self);
      const treeNav = rootStore.treeNav as ITreeNav;
      // nextQuestionKey is a question key: match the node asking it
      const edge = treeNav.tree?.edges.find(e => e.previousKey === rootStore.treeNav.currentNode?.key
        && (e.nextKey === nextQuestionKey || e.next?.questionKey === nextQuestionKey));
      console.log('Choices.setNextQuestion: found edge', JSON.stringify(edge, undefined, 2));
      this.setPending({
        questionId: rootStore.treeNav.content.question._id,
        questionKey: rootStore.treeNav.content.question.key,
        answers: [{
          id: 'dynamic-next-question',
          key: edge?.key,
        }]
      });
    },
    updatePending(answer: Partial<SnapshotIn<IResponse>>) {
      if (self.pending) {
        self.pending = cast({
          ...self.pending,
          ...answer
        });
      } else if (answer.questionId) {
        const rootStore = getRoot<IRootStore>(self);
        const treeNav = rootStore.treeNav as ITreeNav;
        const surveyId = treeNav.content.surveyId;
        const pathId = treeNav.content.path?._id;
        const sessionId = treeNav.content.session?._id;
        self.pending = cast({
          questionId: answer.questionId,
          surveyId,
          pathId,
          sessionId,
          ...answer,
        });
      }
    },
    restorePendingChoicesForQuestion(responseContext: ResponseContext & { questionId: string }) {
      const rootStore = getRoot<IRootStore>(self);
      const treeNav = rootStore.treeNav as ITreeNav;
      responseContext.surveyId = responseContext.surveyId || treeNav.content.surveyId;
      responseContext.pathId = responseContext.pathId || treeNav.content.path?._id;
      responseContext.sessionId = responseContext.sessionId || treeNav.content.session?._id;
      const responsesStore = rootStore.responses;
      const question = rootStore.question.getQuestion(responseContext.questionId);
      if (question?._id) {
        const response = responsesStore.getResponse(responseContext);
        // a response is out of date if any answer is no longer found in the list of possible answers the question
        // this excludes 'specialTypeOfAnswer' whose choices are sometimes not stored in question.answers
        const responseIsOutdated = !response?.specialTypeOfAnswer && !response?.answers?.every(a =>  question?.answers?.some(qa => a.id === qa._id));
        const shouldBeRestored = (response?.answers.length === 0 ||
            response?.answers.some(a => a.key !== 'fail')) && !responseIsOutdated;
        if(shouldBeRestored) {
          self.pending = _.cloneDeep(response);
        } else {
          this.updatePending({questionId: question._id, questionKey: question.key});
        }
        if (responseIsOutdated) {
          responsesStore.removeResponse(responseContext);
        }
      }
    },
    /**
       * Move current choices to survey responses
       */
    finalisePending() {
      if (self.pending) {
        console.log('Choices.finalisePending: finalising choices ', JSON.stringify(self.pending, undefined, 2));
        const rootStore = getRoot<IRootStore>(self);
        const responsesStore = rootStore.responses as IResponseStore;
        responsesStore.saveResponse(detach(self.pending));
      } else {
        console.log('Choices.finalisePending: no choices to finalise');
      }
    },
    consumeFinalised(): IResponse[] {
      const rootStore = getRoot<any>(self);
      const treeNav = rootStore.treeNav as ITreeNav;
      const surveyId = treeNav.content.surveyId;
      if (surveyId) {
        const responsesStore = rootStore.responses as IResponseStore;
        return responsesStore.consumeSurveyResponses(surveyId);
      }
      return [];
    },
    updateLocalFeature() {
      if (!self.pending) {
        return;
      }
      const rootStore = getRoot<IRootStore>(self);
      const featureStore = rootStore.feature;
      const treeNav = rootStore.treeNav as ITreeNav;
      const question = treeNav.content.question;
      if (question && question.feature) {
        if (question.type === 'singleSelectWithRadio') {
          featureStore.updateWithSonar(question.feature, self.pending.valueSonar as number, self.pending.importance as number);
        } else if (question.type === 'range') {
          featureStore.updateWithVal(question.feature, self.pending.value as number);
        } else if (question.type === 'freeText') {
          featureStore.updateWithFreeText(question.feature, self.pending.freeText as string);
        } else if (self.pending.answers.length > 0) {
          const answers = self.pending.answers.map(v => question.answers?.find(a => a._id === v.id)?.featureChoice);
          featureStore.updateWithAnswers(question.feature, answers);
        }
      }
    },
    async updateServerFeature(reckoningId: string | undefined) {
      if (!self.pending) {
        return;
      }
      const rootStore = getRoot<IRootStore>(self);
      const featureStore = rootStore.feature;
      const treeNav = rootStore.treeNav as ITreeNav;
      const question = treeNav.content.question;
      if (!question || !question.feature) {
        return;
      }
      let value = null;
      let answers = null;
      if (question?.type === 'singleSelect' || question?.type === 'multiSelect') {
        if (self.pending.answers.length === 0) {
          return;
        }
        answers = self.pending.answers.map(v => question.answers?.find(a => a._id === v.id)?.featureChoice);
      }
      if (question?.type === 'range') {
        value = { value: self.pending.value as number };
      } else if (question?.type === 'singleSelect' || question?.type === 'singleSelectWithAnswer') {
        value = { valueSingleSelect: answers?.[0] };
      } else if (question?.type === 'singleSelectWithRadio') {
        value = { value: self.pending.valueSonar, importance: self.pending.importance };
      } else if (question?.type === 'multiSelect') {
        value = { valueMultiSelect: answers };
      } else if (question?.type === 'freeText') {
        value = { valueFreeText: self.pending.freeText };
      }
      await featureStore.createFeatureFromQuestion(
        question.feature,
        question._id,
        reckoningId || null,
        value
      );
    },
    updateExercise(exercise: SnapshotIn<IExercise>) {
      console.log('Choices.updateExercise', exercise);
      self.exercise = {
        ...self.exercise,
        ...exercise,
      };
      console.log('Choices.updateExercise now', self.exercise);
    }
  }))
  .views(self => ({
    getFinalised(questionId: string) {
      return this.finalised.find(r => r.questionId === questionId);
    },
    get finalised(): IResponse[] {
      const rootStore = getRoot<IRootStore>(self);
      const surveyId = (rootStore.treeNav as ITreeNav).content.surveyId;
      if (surveyId) {
        const responsesStore = rootStore.responses as IResponseStore;
        return responsesStore.getResponsesForSurvey(surveyId) || [];
      }
      return [];
    }
  }));

export interface IExercise extends Instance<typeof Exercise> {}
